// ============================================
// Motion Blur Post-Process Effect
// ============================================

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { MOTION_BLUR_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Quality presets for Motion Blur
 * Determines the number of samples used
 */
export enum MotionBlurQuality {
  Low = 0,      // 4 samples
  Medium = 1,   // 8 samples
  High = 2,     // 16 samples
  Ultra = 3,    // 32 samples
}

const QUALITY_SAMPLES: Record<MotionBlurQuality, number> = {
  [MotionBlurQuality.Low]: 4,
  [MotionBlurQuality.Medium]: 8,
  [MotionBlurQuality.High]: 16,
  [MotionBlurQuality.Ultra]: 32,
};

/**
 * Motion Blur post-processing effect
 * 
 * Applies directional blur based on camera movement velocity.
 * Supports quality presets and velocity clamping.
 */
export class MotionBlurEffect extends PostProcessEffect {
  readonly type = 'motionBlur';
  readonly displayName = 'Motion Blur';
  
  // Parameters
  intensity = 1.0;           // Blur strength multiplier (0-2)
  quality = MotionBlurQuality.Medium;  // Quality preset
  minVelocity = 0.001;       // Minimum velocity to trigger blur
  maxVelocity = 0.1;         // Maximum velocity (clamped)
  
  // Velocity tracking
  private lastCameraX = 0;
  private lastCameraY = 0;
  private velocityX = 0;
  private velocityY = 0;
  private smoothVelocityX = 0;
  private smoothVelocityY = 0;
  
  // GPU resources  
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = MOTION_BLUR_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      { name: 'intensity', type: 'float', min: 0, max: 2, default: 1.0, description: 'Blur intensity' },
      { name: 'quality', type: 'int', min: 0, max: 3, default: 1, description: 'Quality (0=Low, 1=Medium, 2=High, 3=Ultra)' },
      { name: 'minVelocity', type: 'float', min: 0, max: 0.05, default: 0.001, description: 'Min velocity threshold' },
      { name: 'maxVelocity', type: 'float', min: 0.01, max: 0.5, default: 0.1, description: 'Max velocity clamp' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'intensity': return this.intensity;
      case 'quality': return this.quality;
      case 'minVelocity': return this.minVelocity;
      case 'maxVelocity': return this.maxVelocity;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'intensity': this.intensity = value; break;
      case 'quality': this.quality = Math.floor(Math.max(0, Math.min(3, value))) as MotionBlurQuality; break;
      case 'minVelocity': this.minVelocity = value; break;
      case 'maxVelocity': this.maxVelocity = value; break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const motionBlurWgsl = getCompiledShader('postprocess/motion-blur');
    this.shader = ctx.createShader(motionBlurWgsl, motionBlurWgsl);
    
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'motion-blur-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'motion-blur-ldr',
    });
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, camera, deltaTime, isHdrTarget } = context;
    
    // Calculate velocity from camera movement
    const camX = camera.x;
    const camY = camera.y;
    
    // Velocity in world units per frame
    const rawVelX = (camX - this.lastCameraX);
    const rawVelY = (camY - this.lastCameraY);
    
    // Convert to UV space (normalized by screen size and zoom)
    const screenScale = Math.min(width, height) * camera.zoom;
    this.velocityX = rawVelX / screenScale;
    this.velocityY = rawVelY / screenScale;
    
    // Smooth velocity (temporal filtering to reduce jitter)
    const smoothing = 0.3;
    this.smoothVelocityX = this.smoothVelocityX * (1 - smoothing) + this.velocityX * smoothing;
    this.smoothVelocityY = this.smoothVelocityY * (1 - smoothing) + this.velocityY * smoothing;
    
    this.lastCameraX = camX;
    this.lastCameraY = camY;
    
    // Get sample count from quality preset
    const samples = QUALITY_SAMPLES[this.quality];

    this.uniformWriter.setAll({
      velocity: [this.smoothVelocityX, this.smoothVelocityY],
      // context.intensity already includes this.intensity (applied by PostProcessEffect.render) —
      // multiplying by this.intensity again squared the slider value.
      intensity: intensity,
      samples: samples,
      minVelocity: this.minVelocity,
      maxVelocity: this.maxVelocity,
      _pad0: 0,
      _pad1: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
